import { useEffect, useState } from 'react'
import { Button, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { TwinNote, TwinNoteSummary } from 'api/model'
import AddOrEditNoteModal from 'components/MyTwin/AddOrEditNoteModal'
import { useFetchNotes } from 'hooks/use-notes'
import SearchIcon from '@mui/icons-material/Search'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { COLORS } from 'app-constants'

const AttachNotesPopover = ({
  existingNotes,
  onAddNotes,
  onClose,
}: {
  existingNotes: TwinNoteSummary[]
  onAddNotes: (notes: TwinNoteSummary[]) => void
  onClose: () => void
}) => {
  const [selectedNotes, setSelectedNotes] = useState<TwinNoteSummary[]>([])

  const { notes, isLoading: isLoadingNotes, setNotes } = useFetchNotes()

  useEffect(() => {
    setSelectedNotes(existingNotes)
  }, [existingNotes])

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredNotes = notes.filter((note) => {
    return note.title.toLowerCase().includes(searchText.toLowerCase()) || selectedNotes.find((s) => s.id === note.id)
  })

  // hydrate notes on client side
  const onHydrateNotes = (note: TwinNote) => {
    const updatedNote: TwinNoteSummary = {
      id: note.id,
      title: note.title,
      twin_id: note.twin_id,
      summary: note.content.substring(0, 30), // we need this because the API doesn't return the summary
      date_updated: note.date_updated,
      date_created: note.date_created,
      search_scope: note.search_scope,
    }

    setNotes((prevNotes) => [updatedNote, ...prevNotes])
    setSelectedNotes((prevNotes) => [...prevNotes, updatedNote])
  }

  const [isAddOrEditNoteModalOpen, setIsAddOrEditNoteModalOpen] = useState<boolean>(false)

  const onAddNewNoteClick = () => {
    setIsAddOrEditNoteModalOpen(true)
  }

  // TODO: if loading notes
  if (isLoadingNotes) {
    return null
  }

  return (
    <Stack py={2} px={1} width="320px">
      <Typography textAlign="center" variant="h5">
        Notes
      </Typography>

      <TextField
        placeholder="Search for a note"
        value={searchText}
        onChange={onSearchTextChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  marginRight: '-10px',
                  color: COLORS.GRAY_3,
                  height: '18px',
                  width: '18px',
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          my: 1,
          input: {
            color: 'secondary.main',
          },
          '& ::placeholder': {
            color: 'secondary.main',
          },
        }}
      />

      <Stack
        sx={{
          maxHeight: '300px',
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}>
        {filteredNotes.length === 0 && (
          <Typography
            variant="pXS"
            textAlign="center"
            my={4}
            sx={{
              color: COLORS.GRAY_2,
            }}>
            No notes found
          </Typography>
        )}
        <Stack spacing={1}>
          {filteredNotes.map((f) => {
            const isSelected = selectedNotes.find((s) => s.id === f.id)

            return (
              <Stack
                key={f.id}
                direction="row"
                alignItems="center"
                onClick={() => {
                  const selectedNote = selectedNotes.find((s) => s.id === f.id)
                  if (selectedNote) {
                    setSelectedNotes(selectedNotes.filter((s) => s.id !== f.id))
                  } else {
                    setSelectedNotes([...selectedNotes, f])
                  }
                }}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: isSelected ? COLORS.JELLED_MID_BLUE_10_PERCENT : 'transparent',
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: COLORS.GRAY_8,
                  },
                }}>
                {isSelected ? (
                  <CheckBoxIcon
                    sx={{
                      fill: COLORS.JELLED_PRIMARY_BLUE,
                    }}
                  />
                ) : (
                  <CheckBoxOutlineBlankIcon
                    sx={{
                      fill: COLORS.GRAY_2,
                    }}
                  />
                )}
                <Typography variant="pSM" ml={1} color="secondary">
                  {f.title}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
        <Button
          variant="outlined"
          onClick={onAddNewNoteClick}
          sx={{
            color: COLORS.GRAY_0,
            border: `1px solid ${COLORS.GRAY_5}`,
            '&:hover': {
              borderWidth: '1px',
            },
          }}>
          Add new note
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onAddNotes(selectedNotes)
            onClose()
          }}>
          Done
        </Button>
      </Stack>
      {isAddOrEditNoteModalOpen && (
        <AddOrEditNoteModal
          noteId={''}
          onClose={() => setIsAddOrEditNoteModalOpen(false)}
          onAddNote={onHydrateNotes}
          onEditNote={() => {}}
          onHydrateFullNote={() => {}}
        />
      )}
    </Stack>
  )
}

export default AttachNotesPopover
